import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import TodoAttachment, { TodoFileRef } from "./TodoAttachment";
import { TodoDialog } from "./TodoModals";
import { addFromMessages, createTaskFromMessages, fetchLists, fetchSharedTask, fetchTasks } from "./todoApi";
import {
    dueText,
    errorText,
    getId,
    isListHidden,
    isOverdue,
    repeatLabel,
    sortLists,
    sortTasks,
    statusLabel,
    userName,
    whenText,
} from "./todoUtils";

/* Задачи в чате: просмотр задачи по карточке и «В задачу» из сообщения. */

const useMeId = () => useSelector((state) => String(state.auth?.user?._id || state.auth?.user?.id || ""));

const taskLink = (task) => `/admin/todo?list=${task.listId}&task=${task._id}`;

/**
 * Задача, открытая по карточке из чата. Участники чата её только смотрят;
 * участникам списка показана кнопка «Открыть в задачах».
 */
export function TodoTaskViewModal({ taskId, onClose }) {
    const meId = useMeId();
    const [state, setState] = useState({ loading: true, error: "", data: null });

    useEffect(() => {
        let cancelled = false;
        setState({ loading: true, error: "", data: null });

        fetchSharedTask(taskId)
            .then((data) => {
                if (!cancelled) setState({ loading: false, error: "", data });
            })
            .catch((error) => {
                if (cancelled) return;
                const missing = Number(error?.response?.status || 0) === 404;
                setState({
                    loading: false,
                    error: missing
                        ? "Задача удалена или у вас больше нет к ней доступа."
                        : errorText(error, "Не удалось открыть задачу"),
                    data: null,
                });
            });

        return () => {
            cancelled = true;
        };
    }, [taskId]);

    const { loading, error, data } = state;
    const task = data?.task || null;
    const subtasks = data?.subtasks || [];
    const overdue = task ? isOverdue(task) : false;
    const repeat = task?.repeat?.every && task.repeat.every !== "none" ? repeatLabel(task.repeat.every) : "";

    return (
        <TodoDialog
            title={task?.title || "Задача"}
            wide
            onClose={onClose}
            footer={task ? (
                <>
                    {data.canEdit ? (
                        <Link className="is-primary" to={taskLink(task)} onClick={onClose}>Открыть в задачах</Link>
                    ) : (
                        <span className="todoDialog__hint">Менять задачу могут участники её списка.</span>
                    )}
                    <button type="button" onClick={onClose}>Закрыть</button>
                </>
            ) : null}
        >
            {loading ? <div className="todoDialog__hint">Загружаем задачу…</div> : null}
            {error ? <div className="todoDialog__error" role="alert">{error}</div> : null}

            {task ? (
                <div className="todoView">
                    <div className="todoView__tags">
                        <span className={`is-${task.status}`}>{statusLabel(task.status)}</span>
                        {task.priority === "high" ? <span className="is-high">Важная</span> : null}
                        {data.listTitle ? <span>Список «{data.listTitle}»</span> : null}
                    </div>

                    <dl className="todoView__facts">
                        {task.assignee ? (
                            <>
                                <dt>Исполнитель</dt>
                                <dd>{userName(task.assignee, meId)}</dd>
                            </>
                        ) : null}
                        {task.dueAt ? (
                            <>
                                <dt>Срок</dt>
                                <dd className={overdue ? "is-overdue" : ""}>
                                    {dueText(task.dueAt)}{overdue ? " · просрочено" : ""}{repeat ? ` · ${repeat.toLowerCase()}` : ""}
                                </dd>
                            </>
                        ) : null}
                        {task.createdBy ? (
                            <>
                                <dt>Создал</dt>
                                <dd>{userName(task.createdBy, meId)} · {whenText(task.createdAt)}</dd>
                            </>
                        ) : null}
                        {task.completedAt ? (
                            <>
                                <dt>Готово</dt>
                                <dd>{userName(task.completedBy, meId)} · {whenText(task.completedAt)}</dd>
                            </>
                        ) : null}
                    </dl>

                    {task.description ? <p className="todoView__text">{task.description}</p> : null}

                    {subtasks.length ? (
                        <div className="todoView__block">
                            <h4>Подзадачи</h4>
                            {subtasks.map((item) => (
                                <div key={item._id} className={`todoSub ${item.status === "done" ? "is-done" : ""}`}>
                                    <input type="checkbox" checked={item.status === "done"} readOnly disabled />
                                    <span>{item.title}</span>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {(task.attachments || []).length ? (
                        <div className="todoView__block">
                            <h4>Файлы и голосовые</h4>
                            {task.attachments.map((item) => (
                                <TodoAttachment
                                    key={item._id}
                                    taskId={task._id}
                                    attachment={item}
                                    meId={meId}
                                    commentCount={(task.comments || []).filter((comment) => comment.attachmentId === item._id).length}
                                />
                            ))}
                        </div>
                    ) : null}

                    {(task.comments || []).length ? (
                        <div className="todoView__block">
                            <h4>Комментарии</h4>
                            {task.comments.map((item) => (
                                <div key={item._id} className="todoComment">
                                    <div className="todoComment__head">
                                        <b>{userName(item.author, meId)}</b>
                                        <small>{whenText(item.createdAt)}</small>
                                    </div>
                                    {/* Комментарий к конкретному фото или файлу: показываем, к какому. */}
                                    {item.attachmentId ? (
                                        <TodoFileRef
                                            taskId={task._id}
                                            attachment={(task.attachments || []).find((file) => file._id === item.attachmentId) || null}
                                        />
                                    ) : null}
                                    <p>{item.text}</p>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </TodoDialog>
    );
}

/**
 * «В задачу» из сообщения чата: сделать из него новую задачу или добавить
 * его текст и файл в уже существующую. Файл копируется: удалят сообщение —
 * в задаче он останется.
 */
export function TodoFromMessageModal({ message = null, messages = null, onClose }) {
    const meId = useMeId();
    // Можно сразу несколько сообщений (выбранные в чате) — до 20, как в приложении.
    const items = useMemo(() => (
        (Array.isArray(messages) && messages.length ? messages : (message ? [message] : [])).slice(0, 20)
    ), [message, messages]);
    const messageIds = useMemo(() => items.map((item) => getId(item)).filter(Boolean), [items]);
    const first = items[0] || null;

    const [lists, setLists] = useState([]);
    const [listId, setListId] = useState("");
    const [mode, setMode] = useState("new");
    const [tasks, setTasks] = useState(null);
    const [taskId, setTaskId] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    /** Что получилось: { task, text }. */
    const [done, setDone] = useState(null);

    useEffect(() => {
        let cancelled = false;

        fetchLists()
            .then((result) => {
                if (cancelled) return;
                // Скрытые списки идут в конце: чаще кладут в те, что на виду.
                const sorted = sortLists(result, meId);
                const ordered = [
                    ...sorted.filter((item) => !isListHidden(item, meId)),
                    ...sorted.filter((item) => isListHidden(item, meId)),
                ];
                setLists(ordered);
                setListId(ordered[0]?._id || "");
            })
            .catch((loadError) => {
                if (!cancelled) setError(errorText(loadError, "Не удалось загрузить списки задач"));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [meId]);

    // Для «в существующую» нужны задачи выбранного списка.
    useEffect(() => {
        if (mode !== "existing" || !listId) return undefined;
        let cancelled = false;

        setTasks(null);
        setTaskId("");

        fetchTasks(listId)
            .then((result) => {
                if (cancelled) return;
                const open = sortTasks(result.tasks.filter((item) => !item.parentId && item.status !== "done"));
                setTasks(open);
                setTaskId(open[0]?._id || "");
            })
            .catch((loadError) => {
                if (cancelled) return;
                setTasks([]);
                setError(errorText(loadError, "Не удалось загрузить задачи списка"));
            });

        return () => {
            cancelled = true;
        };
    }, [listId, mode]);

    const preview = useMemo(() => {
        const text = String(first?.text || "").trim();
        const head = text
            ? (text.length > 160 ? `${text.slice(0, 157)}…` : text)
            : first?.entity?.title
                ? first.entity.title
                : first?.fileType === "voice"
                    ? "Голосовое сообщение"
                    : first?.fileName ? `Файл: ${first.fileName}` : "Сообщение";
        return items.length > 1 ? `${head}\n…и ещё сообщений: ${items.length - 1}` : head;
    }, [first, items.length]);

    const submit = async () => {
        if (busy || !messageIds.length) return;
        setBusy(true);
        setError("");

        try {
            if (mode === "new") {
                const task = await createTaskFromMessages(listId, messageIds);
                setDone({ task, text: `Задача создана: «${task.title}»` });
            } else {
                const result = await addFromMessages(taskId, messageIds);
                const parts = [
                    result.files ? `файлов: ${result.files}` : "",
                    result.comments ? `комментариев: ${result.comments}` : "",
                ].filter(Boolean).join(", ");
                setDone({ task: result.task, text: `Добавлено в задачу «${result.task.title}»${parts ? ` (${parts})` : ""}` });
            }
        } catch (submitError) {
            setError(errorText(submitError, "Не удалось добавить сообщение в задачи"));
        } finally {
            setBusy(false);
        }
    };

    const canSubmit = !busy && !!listId && (mode === "new" || !!taskId);

    return (
        <TodoDialog
            title={items.length > 1 ? `Сообщения (${items.length}) — в задачу` : "Сообщение — в задачу"}
            onClose={onClose}
            busy={busy}
            footer={done ? (
                <>
                    <Link className="is-primary" to={taskLink(done.task)} onClick={onClose}>Открыть задачу</Link>
                    <button type="button" onClick={onClose}>Готово</button>
                </>
            ) : (
                <>
                    <button type="button" onClick={onClose} disabled={busy}>Отмена</button>
                    <button type="button" className="is-primary" onClick={submit} disabled={!canSubmit}>
                        {busy ? "Добавляем…" : mode === "new" ? "Создать задачу" : "Добавить в задачу"}
                    </button>
                </>
            )}
        >
            <blockquote className="todoDialog__quote">{preview}</blockquote>

            {done ? (
                <div className="todoDialog__success" role="status">{done.text}</div>
            ) : loading ? (
                <div className="todoDialog__hint">Загружаем списки…</div>
            ) : (
                <>
                    <div className="todoDialog__modes" role="radiogroup" aria-label="Куда добавить">
                        <label className={mode === "new" ? "is-active" : ""}>
                            <input type="radio" name="todo-from-mode" checked={mode === "new"} onChange={() => setMode("new")} />
                            <span>
                                <b>Новая задача</b>
                                <small>Первая строка текста станет названием, остальное — описанием, файл — вложением.</small>
                            </span>
                        </label>
                        <label className={mode === "existing" ? "is-active" : ""}>
                            <input type="radio" name="todo-from-mode" checked={mode === "existing"} onChange={() => setMode("existing")} />
                            <span>
                                <b>В существующую</b>
                                <small>Файл ляжет во вложения задачи, текст — в комментарии.</small>
                            </span>
                        </label>
                    </div>

                    <label className="todoField">
                        <span>Список</span>
                        <select value={listId} onChange={(event) => setListId(event.target.value)}>
                            {lists.map((item) => (
                                <option key={item._id} value={item._id}>{item.title}</option>
                            ))}
                        </select>
                    </label>

                    {mode === "existing" ? (
                        <label className="todoField">
                            <span>Задача</span>
                            {tasks === null ? (
                                <span className="todoDialog__hint">Загружаем задачи…</span>
                            ) : tasks.length ? (
                                <select value={taskId} onChange={(event) => setTaskId(event.target.value)}>
                                    {tasks.map((item) => (
                                        <option key={item._id} value={item._id}>{item.title}</option>
                                    ))}
                                </select>
                            ) : (
                                <span className="todoDialog__hint">В этом списке нет невыполненных задач.</span>
                            )}
                        </label>
                    ) : null}
                </>
            )}

            {error ? <div className="todoDialog__error" role="alert">{error}</div> : null}
        </TodoDialog>
    );
}
